import { useEffect, useRef } from 'react';

/**
 * 검색창 단축키(⌘K / Ctrl+K) — 시안 `.toolbar .search`의 `.kbd` 배지와 짝.
 *
 * 훅이 돌려준 ref를 검색 input에 걸면, 페이지 어디서든 단축키로 그 입력에 포커스가 온다.
 * 브라우저 기본(⌘K=주소창 검색 등)을 가로채므로 **페이지의 주 검색창 하나에만** 쓴다 —
 * 한 화면에서 두 번 호출하면 나중 등록이 이긴다는 보장이 없다.
 */
export function useSearchHotkey() {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        ref.current?.focus();
        ref.current?.select();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return ref;
}

/** 플랫폼별 표기 — mac 계열만 ⌘, 그 외(윈도우·리눅스)는 Ctrl. SSR 없음(web은 CSR 전용). */
function hotkeyLabel(): string {
  const platform = typeof navigator !== 'undefined' ? navigator.platform : '';
  return /Mac|iPhone|iPad/.test(platform) ? '⌘ K' : 'Ctrl K';
}

/**
 * 시안 `.kbd` — 검색창 우측의 단축키 배지. 장식이므로 스크린리더에는 숨긴다
 * (동작은 훅이 전역 리스너로 제공하고, 배지는 그 존재를 눈에만 알린다).
 */
export function SearchHotkeyBadge() {
  return (
    <span
      aria-hidden="true"
      className="hidden flex-none items-center rounded-[5px] border border-line bg-white px-1.5 py-px text-xs font-bold text-ink-400 sm:inline-flex"
    >
      {hotkeyLabel()}
    </span>
  );
}
